import { useQuery } from "react-query";
import { loggedInApiCall } from "src/api/axiosClient/userApi";
import { parseMenuTreeResponse } from "./parseMenuTreeResponse";

export type MenuItem = {
    id: number;
    label: string;
    i18nKey?: string;
    weight: number;
    url?: string;
    icon?: string;
    moduleFeatureKey?: string;
    isActive: boolean;
};

export type MenuTreeNode = {
    item: MenuItem;
    children: MenuTreeNode[];
};

export type MenuTreeResponse = {
    tree: MenuTreeNode[];
    meta: {
        total_items: number;
        max_depth: number;
        last_updated: string;
    };
};

export const createEmptyMenuTreeResponse = (): MenuTreeResponse => ({
    tree: [],
    meta: {
        total_items: 0,
        max_depth: 0,
        last_updated: new Date().toISOString(),
    },
});

export const getNavigationMenuQueryKey = "getNavigationMenu" as const;

export const useNavigationMenu = (organizationId: number | undefined) => {
    const query = useQuery({
        queryKey: [getNavigationMenuQueryKey, organizationId],
        queryFn: async (): Promise<MenuTreeResponse> => {
            if (organizationId === undefined) {
                return createEmptyMenuTreeResponse();
            }

            const response = await loggedInApiCall.get(`/api/organizations/${organizationId}/menus/main/tree`);

            return parseMenuTreeResponse(response.data);
        },
        enabled: organizationId !== undefined,
        staleTime: 30000,
        cacheTime: 300000,
    });

    return {
        menuTree: query.data?.tree || [],
        meta: query.data?.meta || { total_items: 0, max_depth: 0, last_updated: new Date().toISOString() },
        isLoading: query.isLoading,
        error: query.error,
        refetch: query.refetch,
    };
};
